import React from 'react'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { Box, Flex, Text } from 'rebass'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import { useNewTranslation, NewTrans } from 'hooks/index'
import Inputs from 'components/Inputs'
import { theme } from 'theme'
import Button from 'components/Shared/Button/OldButton'
import ButtonFa from 'components/ButtonFa'
import { CampaignState } from 'state/campaigns'
import { StyledPopup, PartiTooltip, TooltipButton } from '../style'

interface EditSlotsFormPopUpProps {
    campaignContent: CampaignState
    onOpen: () => void
    onSubmit: (values: any, lastEditSlots: any) => void
}

const EditSlotsFormPopUp: React.FC<EditSlotsFormPopUpProps> = ({ campaignContent, onOpen, onSubmit }) => {
    const [newT] = useNewTranslation()
    const lastEditSlots =
        campaignContent && typeof campaignContent.registration_max_slots === 'number'
            ? campaignContent.registration_max_slots
            : 0

    const FormSchema = yup.object({
        registration_max_slots: yup
            .number()
            .typeError(newT('errorMessage.businessLogic.enterWholeNumber'))
            .required(newT('errorMessage.validation.enterNumberSlots'))
            .positive(newT('errorMessage.businessLogic.enterNonZeroSlots')),
    })

    return (
        <StyledPopup
            modal
            trigger={
                <TooltipButton
                    p={0}
                    m={0}
                    width="100%"
                    textProps={{ fontWeight: 'normal', color: theme.colors.black, textAlign: 'left' }}
                >
                    {newT('button.editRegistrationSlot', 'Edit registration slots')}
                </TooltipButton>
            }
            onOpen={() => {
                onOpen()
            }}
            closeOnEscape={false}
            closeOnDocumentClick={false}
        >
            {close => (
                <Formik
                    enableReinitialize={true}
                    validationSchema={FormSchema}
                    initialValues={{
                        registration_max_slots: lastEditSlots,
                    }}
                    onSubmit={(values: any) => {
                        onSubmit(values, lastEditSlots)
                        close()
                    }}
                >
                    <Form>
                        <PartiTooltip m={theme.spacing.xs} className="box">
                            <ButtonFa
                                icon={faTimes}
                                iconSize="m"
                                iconColor="gray"
                                onClick={close}
                                className={'close__button'}
                            />
                            <Text fontWeight="bold" my={theme.spacing.xxs}>
                                {newT(
                                    'modalWindow.confirmationMessage.editRegistrationSlotTitle',
                                    'Enable registration for this campaign',
                                )}
                            </Text>
                            <NewTrans i18nKey="modalWindow.confirmationMessage.editRegistrationSlotSubtitle">
                                <Text fontSize="0.9em" display="inline">
                                    If enabled, this campaign will have an interest phase in which Partiposters are
                                    required to submit their profile info. Only Partiposters approved in the interest
                                    phase will be allowed to continue with the remaining tasks in the campaign.{' '}
                                </Text>
                                <Text fontSize="0.9em" fontWeight="bold" display="inline">
                                    You cannot change this setting once the campaign is live.
                                </Text>
                            </NewTrans>

                            <Box display="flex">
                                <Inputs.Label
                                    width="84px"
                                    textAlign="left"
                                    textColor={'mineShaft'}
                                    text={newT('campaignEditor.setUpCampaign.enterRegistrationSlotsHeader')}
                                />
                                <Box mr={'44px'} />
                                <Inputs.Number
                                    name="registration_max_slots"
                                    data-testid="registration-max-slot"
                                    disableDefaultStyle={true}
                                    disabled={false}
                                />
                            </Box>
                            <Box mb={theme.spacing.xxs} ml={'128px'}>
                                <NewTrans i18nKey="modalWindow.editRegistrationSlotSubtitleInstruction">
                                    <Text fontSize="0.9em" color={theme.colors.mineShaft}>
                                        Enter the total number of slots available for approved registrations.
                                    </Text>
                                    <Text fontSize="0.9em" color={theme.colors.mineShaft}>
                                        Once all registration slots have been filled, Partiposters can be added to a
                                        waitlist
                                    </Text>
                                    <Text fontSize="0.9em" color={theme.colors.mineShaft}>
                                        where they will be approved whenever a registration slot becomes available.
                                    </Text>
                                </NewTrans>
                            </Box>

                            <Flex my={theme.spacing.xs} flexDirection="row" justifyContent="space-around">
                                <Button bg={theme.colors.selago} onClick={close}>
                                    {newT('button.close')}
                                </Button>
                                <Button
                                    bg={theme.colors.astronaut}
                                    textProps={{ color: theme.newColors.neutral50 }}
                                    type="submit"
                                >
                                    {newT('button.confirm')}
                                </Button>
                            </Flex>
                        </PartiTooltip>
                    </Form>
                </Formik>
            )}
        </StyledPopup>
    )
}

export default EditSlotsFormPopUp
